import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.xl};
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: ${theme.shadows.large};
  animation: slideUp 0.3s ease-in-out;

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const Emoji = styled.div`
  font-size: 48px;
  margin-bottom: ${theme.spacing.md};
`;

const Title = styled.h2`
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.xlarge};
  color: ${theme.colors.deepNavy};
  margin: 0 0 ${theme.spacing.sm} 0;
  font-weight: bold;
`;

const Message = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
  line-height: 1.6;
  margin: 0 0 ${theme.spacing.lg} 0;
`;

const ButtonPrimary = styled.button`
  background: linear-gradient(135deg, ${theme.colors.brandYellow} 0%, #FFC700 100%);
  color: ${theme.colors.deepNavy};
  border: none;
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-bottom: ${theme.spacing.sm};
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 8px rgba(255, 209, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(255, 209, 0, 0.3);
  }
`;

const ButtonSecondary = styled.button`
  background-color: transparent;
  color: ${theme.colors.slateGray};
  border: 1px solid ${theme.colors.softGray};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${theme.colors.background};
    border-color: ${theme.colors.slateGray};
  }
`;

const getEncouragementContent = (userLevel, suggestionLevel) => {
  // Streetlight → Daylight
  if (userLevel === 'Streetlight' && suggestionLevel === 'Daylight') {
    return {
      emoji: '🎉',
      title: 'Way to Go!',
      message: "You're challenging yourself with a Daylight level activity! Daylight activities involve collaborative community work - a great step up from personal awareness actions. After you complete this activity, consider updating your level to reflect your growing involvement. You've got this! 💪"
    };
  }

  // Streetlight → Sunshield
  if (userLevel === 'Streetlight' && suggestionLevel === 'Sunshield') {
    return {
      emoji: '🌟',
      title: 'Wow! That\'s Amazing!',
      message: "You're jumping into a Sunshield level activity - that's systems-level advocacy! Sunshield activities involve strategic organizing, policy work, and community leadership. This is a big step! Take your time, reach out for support if needed, and be proud of taking on this challenge."
    };
  }

  // Daylight → Sunshield
  if (userLevel === 'Daylight' && suggestionLevel === 'Sunshield') {
    return {
      emoji: '🚀',
      title: 'Stepping Up!',
      message: "You're taking on a Sunshield level activity! Sunshield activities involve strategic systems change work. You're moving from collaborative action to leadership and policy advocacy. After you complete this, you might want to update your level to recognize your expanding impact."
    };
  }

  // Default (shouldn't happen, but just in case)
  return {
    emoji: '💪',
    title: 'Great Choice!',
    message: "You're taking on a new challenge! Remember, all advocacy matters and you can always update your level later."
  };
};

const EncouragementModal = ({ userLevel, suggestionLevel, onProceed, onGoBack }) => {
  const content = getEncouragementContent(userLevel, suggestionLevel);

  return (
    <Overlay onClick={onGoBack}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Emoji>{content.emoji}</Emoji>
        <Title>{content.title}</Title>
        <Message>{content.message}</Message>

        <ButtonPrimary onClick={onProceed}>
          Log This Activity
        </ButtonPrimary>

        <ButtonSecondary onClick={onGoBack}>
          Go Back
        </ButtonSecondary>
      </Modal>
    </Overlay>
  );
};

export default EncouragementModal;
